import {
  type CellData,
  type ColumnDef,
  flexRender,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
  useTable,
} from "@tanstack/react-table";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTableColumnHeader } from "./data-table-column-header";
import { type DataTableAction, DataTableHeader } from "./data-table-header";
import { DataTablePagination, type ServerSidePaginationState } from "./data-table-pagination";
import { type DataTableFacet, type DataTableFilter, DataTableToolbar } from "./data-table-toolbar";
import { ExportMenu } from "./export-menu";
import type { ExportRow } from "./export-utils";
import { dataTableFeatures } from "./features";
import {
  applyPageSelection,
  computeSelectedCount,
  createPageRowSelection,
  EMPTY_SELECTION,
  type TableSelectionState,
} from "./selection-state";

export type { DataTableAction, DataTableFacet, DataTableFilter };

export interface DataTableBulkSelectionContext {
  mode: "explicit" | "all-filtered";
  selectedIds: string[];
  excludedIds: string[];
  selectedCount: number;
  query: {
    filter?: string;
    search?: string;
    activeFacets?: Record<string, string[]>;
  };
}

export interface DataTableBulkAction<_TData> {
  label: string;
  onClick: (context: DataTableBulkSelectionContext) => void;
  icon?: React.ComponentType<{ className?: string }>;
  variant?: "default" | "outline" | "ghost" | "destructive";
  disabled?: boolean | ((context: DataTableBulkSelectionContext) => boolean);
}

export interface DataTableProps<TData extends RowData, TValue extends CellData> {
  title?: string;
  subtitle?: string;
  badge?: number | string;
  actions?: DataTableAction[];
  headerExtra?: ReactNode;
  filters?: DataTableFilter[];
  filter?: string;
  onFilterChange?: (value: string) => void;
  filterVariant?: "tabs" | "dropdown";
  facets?: DataTableFacet[];
  activeFacets?: Record<string, string[]>;
  onFacetChange?: (column: string, values: string[]) => void;
  searchPlaceholder?: string;
  search?: string;
  onSearchChange?: (value: string) => void;
  exportFileName?: string;
  exportRows?: ExportRow[];
  bulkActions?: DataTableBulkAction<TData>[];
  columns: ColumnDef<typeof dataTableFeatures, TData, TValue>[];
  data: TData[];
  rowIdAccessor?: (row: TData) => string;
  /** When set, clicking a row (outside an interactive control) invokes this. */
  onRowClick?: (row: TData) => void;
  filteredRowCount?: number;
  pageCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  enableRowSelection?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onSelectionChange?: (rows: TData[]) => void;
  onSelectionStateChange?: (state: TableSelectionState) => void;
  selectionResetKey?: string | number;
  /**
   * When true, the table trusts the parent to have already paginated, sorted,
   * and filtered the `data` prop. TanStack runs in `manualPagination` /
   * `manualSorting` / `manualFiltering` mode; sorting and pagination changes
   * are surfaced via the existing `onSortingChange` / `onPaginationChange`
   * callbacks but no client-side row models are computed.
   */
  serverSide?: boolean;
  /**
   * Server-side pagination state. Required when `serverSide` is true and
   * pagination is desired. Drives the bottom pagination footer (totals, page
   * number, prev/next buttons).
   */
  serverSidePagination?: ServerSidePaginationState;
}

export function DataTable<TData extends RowData, TValue extends CellData>({
  title,
  subtitle,
  badge,
  actions,
  headerExtra,
  filters,
  filter,
  onFilterChange,
  filterVariant,
  facets,
  activeFacets,
  onFacetChange,
  searchPlaceholder,
  search,
  onSearchChange,
  exportFileName,
  exportRows,
  bulkActions,
  columns,
  data,
  rowIdAccessor,
  onRowClick,
  filteredRowCount,
  pageCount,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  enableRowSelection = false,
  enableSorting = true,
  enablePagination = false,
  isLoading = false,
  isError = false,
  onSelectionChange,
  onSelectionStateChange,
  selectionResetKey,
  serverSide = false,
  serverSidePagination,
}: DataTableProps<TData, TValue>) {
  const [selectionState, setSelectionState] = useState<TableSelectionState>(EMPTY_SELECTION);
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  const sortingState = sorting ?? internalSorting;
  const totalFilteredRows = filteredRowCount ?? (typeof badge === "number" ? badge : undefined);

  const getRowId = useCallback(
    (row: TData, index: number) => (rowIdAccessor ? rowIdAccessor(row) : String(index)),
    [rowIdAccessor],
  );

  const pageRowIds = useMemo(
    () => data.map((row, index) => getRowId(row, index)),
    [data, getRowId],
  );

  const rowSelection = useMemo(
    () => createPageRowSelection(selectionState, pageRowIds),
    [selectionState, pageRowIds],
  );

  const selectedCount = computeSelectedCount(selectionState, totalFilteredRows);

  const selectionContext: DataTableBulkSelectionContext = {
    mode: selectionState.mode,
    selectedIds: selectionState.selectedIds,
    excludedIds: selectionState.excludedIds,
    selectedCount,
    query: {
      filter,
      search,
      activeFacets,
    },
  };

  const toolbarBulkActions = bulkActions?.map((action) => ({
    label: action.label,
    icon: action.icon,
    variant: action.variant,
    disabled:
      typeof action.disabled === "function" ? action.disabled(selectionContext) : action.disabled,
    onClick: () => action.onClick(selectionContext),
  }));

  const setAndNotifySelection = useCallback(
    (nextSelection: TableSelectionState) => {
      setSelectionState(nextSelection);
      onSelectionStateChange?.(nextSelection);
    },
    [onSelectionStateChange],
  );

  useEffect(() => {
    if (selectionResetKey === undefined) {
      return;
    }

    setAndNotifySelection(EMPTY_SELECTION);
    onSelectionChange?.([]);
  }, [selectionResetKey, onSelectionChange, setAndNotifySelection]);

  const allColumns = useMemo<ColumnDef<typeof dataTableFeatures, TData, TValue>[]>(() => {
    const result: ColumnDef<typeof dataTableFeatures, TData, TValue>[] = [];

    if (enableRowSelection) {
      result.push({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      } as ColumnDef<typeof dataTableFeatures, TData, TValue>);
    }

    result.push(
      ...(columns.map((col) => {
        if (enableSorting && col.header && typeof col.header === "string") {
          const headerTitle = col.header;
          return {
            ...col,
            header: ({ column }: { column: unknown }) => (
              <DataTableColumnHeader column={column as never} title={headerTitle} />
            ),
          };
        }
        return col;
      }) as ColumnDef<typeof dataTableFeatures, TData, TValue>[]),
    );

    return result;
  }, [columns, enableRowSelection, enableSorting]);

  const table = useTable({
    features: dataTableFeatures,
    data,
    // The table's own column list is heterogeneous, so v9 types it at
    // TValue = unknown. ColumnDef is invariant in TValue, so a narrower
    // per-column TValue is not assignable without this widening.
    columns: allColumns as ColumnDef<typeof dataTableFeatures, TData, unknown>[],
    state: {
      sorting: sortingState,
      pagination: pagination || { pageIndex: 0, pageSize: 10 },
      rowSelection,
    },
    pageCount: pageCount || -1,
    onSortingChange: (updater) => {
      const nextSorting = typeof updater === "function" ? updater(sortingState) : updater;
      setInternalSorting(nextSorting);
      onSortingChange?.(nextSorting);
    },
    onPaginationChange,
    onRowSelectionChange: (updater) => {
      const nextPageSelection = typeof updater === "function" ? updater(rowSelection) : updater;
      const nextSelectionState = applyPageSelection(selectionState, pageRowIds, nextPageSelection);
      setAndNotifySelection(nextSelectionState);

      if (onSelectionChange) {
        const selectedRows = data.filter((row, index) => {
          const id = getRowId(row, index);
          return !!nextPageSelection[id];
        });
        onSelectionChange(selectedRows);
      }
    },
    // v9 registers row models statically in `features` rather than per-option.
    // Server-side mode used to omit the sorted/paginated models entirely; the
    // manual* flags below short-circuit them instead, which is equivalent.
    manualSorting: serverSide,
    manualPagination: serverSide,
    // No `manualFiltering`: the filtering features are not registered at all
    // (search and filters are server-side), so react-table cannot filter and
    // the opt-out has nothing to switch off.
    enableRowSelection,
    getRowId,
  });

  const showHeader = !!title || !!subtitle || badge != null || !!actions?.length;
  const showToolbar =
    !!filters?.length ||
    !!facets?.length ||
    !!onSearchChange ||
    (selectedCount > 0 && !!bulkActions?.length);

  const combinedHeaderExtra =
    exportFileName && exportRows ? (
      <>
        {headerExtra}
        <ExportMenu fileName={exportFileName} rows={exportRows} />
      </>
    ) : (
      headerExtra
    );

  return (
    <div className="[&_button]:shadow-none [&_input]:shadow-none [&_textarea]:shadow-none [&_[data-slot=select-trigger]]:shadow-none [&_[data-slot=card]]:shadow-2xs">
      {showHeader && (
        <DataTableHeader
          title={title}
          subtitle={subtitle}
          badge={badge}
          actions={actions}
          headerExtra={combinedHeaderExtra}
        />
      )}
      <div className={showHeader ? "mt-8" : ""}>
        {showToolbar && (
          <DataTableToolbar
            filters={filters}
            activeFilter={filter}
            onFilterChange={onFilterChange}
            filterVariant={filterVariant}
            facets={facets}
            activeFacets={activeFacets}
            onFacetChange={onFacetChange}
            search={search}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
            selectedCount={selectedCount}
            bulkActions={toolbarBulkActions}
            filteredRowCount={totalFilteredRows}
            isAllFilteredSelected={selectionState.mode === "all-filtered"}
            onSelectAllFiltered={() =>
              setAndNotifySelection({
                mode: "all-filtered",
                selectedIds: [],
                excludedIds: [],
              })
            }
            onClearSelection={() => {
              setAndNotifySelection(EMPTY_SELECTION);
              onSelectionChange?.([]);
            }}
          />
        )}

        <div
          className={
            showToolbar ? "mt-4 rounded-md border border-border" : "rounded-md border border-border"
          }
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={allColumns.length} className="h-24 text-center">
                    <div className="text-muted-foreground">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={allColumns.length} className="h-24 text-center">
                    <div className="text-destructive">Unable to load data.</div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={allColumns.length} className="h-24 text-center">
                    <div className="text-muted-foreground">No results.</div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={onRowClick ? "cursor-pointer" : undefined}
                    onClick={
                      onRowClick
                        ? (e) => {
                            // Ignore clicks that land on an interactive control
                            // (row actions menu, links, checkboxes, inputs).
                            if (
                              (e.target as HTMLElement).closest(
                                "button, a, input, [role='menuitem'], [role='checkbox']",
                              )
                            ) {
                              return;
                            }
                            onRowClick(row.original);
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      {enablePagination && (serverSidePagination || table.getPageCount() > 1) && (
        <div className="mt-4">
          <DataTablePagination
            table={table}
            showSelection={enableRowSelection}
            serverSide={serverSidePagination}
          />
        </div>
      )}
    </div>
  );
}
